import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../services/auth/auth.service';
import { SnackbarService } from '../services/snackbar/snackbar.service';
import { TranslateService } from '@ngx-translate/core';
import {
  AppLanguage,
  LanguageService,
  SUPPORTED_LANGUAGES,
} from '../services/language/language.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
})
export class LoginComponent implements OnInit {
  username = '';
  password = '';
  showPassword = false;
  loading = false;
  recoveryMode = false;
  recoveryIdentifier = '';
  recoveryLoading = false;
  recoverySent = false;
  readonly languages = SUPPORTED_LANGUAGES;

  constructor(
    private authService: AuthService,
    private snackbar: SnackbarService,
    private router: Router,
    private translate: TranslateService,
    private languageService: LanguageService,
  ) {}

  async ngOnInit(): Promise<void> {
    if (await this.authService.ensureSession()) {
      this.router.navigate([this.postLoginRoute()]);
    }
  }

  get currentLanguage(): AppLanguage {
    return this.languageService.current();
  }

  changeLanguage(language: AppLanguage): void {
    this.languageService.use(language);
  }

  private postLoginRoute(): string {
    return this.authService.mustChangePassword()
      ? '/change-password'
      : this.authService.getDefaultRoute();
  }

  async submit(): Promise<void> {
    if (!this.username || !this.password || this.loading) {
      return;
    }

    this.loading = true;

    try {
      const username = this.username.trim();
      await this.authService.login(username, this.password);
      this.router.navigate([this.postLoginRoute()]);
    } catch (error: any) {
      const status = error?.status;
      const message =
        status === 429
          ? this.translate.instant('login.errors.rateLimit')
          : status === 403
          ? error?.error?.message || this.translate.instant('login.errors.forbidden')
          : this.translate.instant('login.errors.invalid');
      this.snackbar.error(message, { duration: 4000 });
    } finally {
      this.loading = false;
    }
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  openRecovery(): void {
    this.recoveryIdentifier = this.username.trim();
    this.recoverySent = false;
    this.recoveryMode = true;
  }

  closeRecovery(): void {
    this.recoveryMode = false;
    this.recoverySent = false;
  }

  async submitRecovery(): Promise<void> {
    const identifier = this.recoveryIdentifier.trim();
    if (!identifier || this.recoveryLoading) {
      return;
    }

    this.recoveryLoading = true;
    try {
      await this.authService.requestPasswordReset(identifier);
      this.recoverySent = true;
    } catch (error: any) {
      const message =
        error?.status === 429
          ? this.translate.instant('login.errors.resetRateLimit')
          : this.translate.instant('login.errors.reset');
      this.snackbar.error(message, { duration: 4000 });
    } finally {
      this.recoveryLoading = false;
    }
  }
}
